import { ChartsService } from './charts.service';
import { TranslateService } from '@ngx-translate/core';
import { PerimetersService } from './perimeters.service';
import { APIService } from './../api/api.service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { KPI } from '../api';
import { first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ScopeService } from '.';
import { ObservableMap } from './spreadsheet-columns.service';
import * as R from 'ramda';

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {

  private probabilityMap = {
    [KPI.Confidence.LOW]: this.translate.instant('asset.confidence_levels.low'),
    [KPI.Confidence.MEDIUM]: this.translate.instant('asset.confidence_levels.medium'),
    [KPI.Confidence.HIGH]: this.translate.instant('asset.confidence_levels.high'),
  };

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
    private translate: TranslateService,
    private scopeService: ScopeService,
    private chartsService: ChartsService,
  ) { }

  public getProject(projectId: number): Observable<KPI.Project> {
    return this.perimetersService.perimeters$.pipe(
      first(),
      switchMap((perimeters) =>
        combineLatest([
          of(perimeters),
          this.api.get_project(projectId, {
            buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
          })
        ])
      ),
    ).pipe(
      map(([perimeters, project]) => {
        let perimeter_object: KPI.Perimeter;
        perimeters.map((p) => {
          if (!perimeter_object) {
            if (p.id === project.perimeter) {
              perimeter_object = p;
            } else {
              p.sub_perimeters.map((sp) => {
                if (!perimeter_object) {
                  if (!perimeter_object && sp.id === project.perimeter) {
                    perimeter_object = {
                      ...sp,
                      parent: p,
                    };
                  }
                }
              });
            }
          }
        });

        return {
          ...project,
          perimeter_object,
          probability_label: this.probabilityMap[project.probability],
        };
      })
    )
  }


  private assetProjects: ObservableMap<KPI.Project[]> = {};
  public getAssetProjects(asset: KPI.MappedAsset): Observable<KPI.Project[]> {
    const key = this.getKey(arguments);
    if (!this.assetProjects[key]) {
      this.assetProjects[key] = this.api.get_projects({
        buildings: PerimetersService.getBuildingsIds(asset.perimeters).join(','),
      }).pipe(
        map((projects) => {
          return projects.map((project) => ({
            ...project,
            probability_label: this.probabilityMap[project.probability],
          }));
        }),
        shareReplay(1),
      )
    }
    return this.assetProjects[key];
  }

  private _refreshProjects$ = new BehaviorSubject<void>(null);
  public refreshProjects(): void {
    this._refreshProjects$.next(null);
  }
  public getAllProjects(): Observable<KPI.Project[]> {
    return this._refreshProjects$.pipe(
      switchMap(() => this.perimetersService.perimeters$),
      switchMap((allPerimeters) =>
        this.getPerimeterProjects(allPerimeters).pipe(
          map((projects) => projects.map((project) => {
            const projectPerimeters = PerimetersService.findPerimetersList(project.perimeter, allPerimeters);
            const perimeter_object = projectPerimeters.find((p) => p.id === project.perimeter);
            const site = projectPerimeters.find((p) => !p.is_monosite);
            return {
              ...project,
              perimeter_object,
              site,
            };
          })),
        )
      ),
    );
  }

  // get all projects, forget about the perimeter argument
  public getPerimeterProjects(perimeter: KPI.Perimeter[]): Observable<KPI.Project[]> {
    console.log('even though we have perimeter, we dont use it', perimeter);
    return this.api.get_projects({}).pipe(
      map((projects) => {
        return projects.map((project) => ({
          ...project,
          probability_label: this.probabilityMap[project.probability],
        }));
      }),
    );
  }

  private investmentProjects: ObservableMap<KPI.Project[]> = {};
  public getInvestmentProjects(investment: KPI.Investment): Observable<KPI.Project[]> {
    const key = this.getKey(arguments);
    if (!this.investmentProjects[key]) {
      this.investmentProjects[key] = this.perimetersService.perimeters$.pipe(
        switchMap(() =>
          combineLatest([
            this.api.get_projects({
              investment: investment.id,
            }),
            this.scopeService.investmentStatuses$,
          ])
        ),
      ).pipe(
        map(([projects]) => {
          return projects.map((project) => ({
            ...project,
            probability_label: this.probabilityMap[project.probability],
          }));
        }),
        shareReplay(1),
      );
    }
    return this.investmentProjects[key];
  }

  private projectTypes: Observable<KPI.ProjectType[]>;
  public get projectTypes$(): Observable<KPI.ProjectType[]> {
    if (!this.projectTypes) {
      this.projectTypes = this.api.get_project_types().pipe(shareReplay(1));
    }
    return this.projectTypes;
  }

  /**
   * Updates an project in the data base
   *
   * @param id
   * @param updatedProject
   */
  public updateProject(
    id: number,
    updatedProject: Partial<KPI.Project>,
  ): Observable<KPI.Project> {
    return this.api.project_patch(id, updatedProject);
  }

  public createProject(project: Partial<KPI.Project>): Observable<KPI.Project> {
    return this.api.project_post(project);
  }

  public deleteProject(projectId: number): Observable<void> {
    return this.api.project_delete(projectId);
  }

  /**
   * Updates an project in the data base
   *
   * @param id
   * @param updatedProject
   */
  public updateProjectInstance(
    projectId: number,
    updatedInstances: Partial<KPI.WriteableProjectInstance[]>,
  ): Observable<KPI.Project> {
    return this.api.project_instances_patch(projectId, updatedInstances);
  }

  public updateProjectGlobalGain(projectId: number, value: boolean): void {
    this.api.project_post_global_gain(projectId, value).subscribe({
      next: () => this.chartsService.energyHasChanged(),
    });
  }

  public updateProjectGains(projectId: number, gains: KPI.ProjectGain[]): Observable<KPI.Project> {
    return this.api.project_post_gains(projectId, gains).pipe(
      tap(() => this.chartsService.energyHasChanged()),
    );
  }

  public updateProjectInitiativesAndInvestments(
    projectId: number,
    investments: number[],
    initiatives: number[]
  ): Observable<KPI.Project> {
    return this.api.project_post_initiatives_and_investments(projectId, { initiatives, investments });
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }
}
